import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { triggerDownload } from "@/lib/download";
import Header from "@/components/Header";
import {
  ArrowLeft,
  Upload,
  Trash2,
  FileText,
  Download,
  RefreshCw,
  AlertTriangle,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  CheckSquare,
  Square,
} from "lucide-react";

const MESI = [
  "", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function Dettaglio() {
  const { codice, anno, mese } = useParams();
  const nav = useNavigate();
  const [detail, setDetail] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api
      .get(`/acquisizioni/by-cliente/${codice}/${anno}/${mese}`)
      .then((r) => setDetail(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || "Errore caricamento"),
      );
  }, [codice, anno, mese]);

  const loadLogs = useCallback(() => {
    if (!detail) return;
    api
      .get(`/acquisizioni/${detail.id}/logs`)
      .then((r) => setLogs(r.data))
      .catch(() => {});
  }, [detail]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (logsOpen) loadLogs();
  }, [logsOpen, loadLogs]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      await api.post(`/acquisizioni/${detail.id}/pdfs`, fd);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const deletePdf = async (pdf_id) => {
    if (!window.confirm("Rimuovere questo PDF?")) return;
    await api.delete(`/acquisizioni/pdfs/${pdf_id}`);
    load();
  };

  const generate = async () => {
    setGenerating(true);
    setError("");
    try {
      const r = await api.post(`/acquisizioni/${detail.id}/generate`);
      setDetail(r.data);
      if (logsOpen) loadLogs();
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore generazione");
      load();
    } finally {
      setGenerating(false);
    }
  };

  const download = () => {
    setError("");
    try {
      const token = localStorage.getItem("pa_token") || "";
      const url = `${API}/acquisizioni/${detail.id}/download?token=${encodeURIComponent(token)}`;
      const ok = triggerDownload(url, detail.excel_filename || "paghe.xlsx");
      if (!ok) {
        setError(
          "Download non avviato. Prova ad aprire l'app in una nuova scheda del browser.",
        );
      }
    } catch (e) {
      setError(`Download fallito: ${e.message || e}`);
    }
  };

  const toggleExternal = async () => {
    setError("");
    try {
      const flag = detail.status !== "acquisito_esterno";
      const r = await api.post(
        `/acquisizioni/by-cliente/${codice}/${anno}/${mese}/mark-external`,
        { flagged: flag },
      );
      setDetail(r.data);
      if (logsOpen) loadLogs();
    } catch (e) {
      setError(e?.response?.data?.detail || "Operazione non riuscita");
    }
  };

  if (!detail) {
    return (
      <div>
        <Header />
        <div className="pa-container" style={{ padding: 40, color: "var(--pa-muted)" }}>
          {error || "Caricamento…"}
        </div>
      </div>
    );
  }

  const sbil =
    detail.quadratura_dare != null && detail.quadratura_avere != null
      ? +(detail.quadratura_dare - detail.quadratura_avere).toFixed(2)
      : null;
  const quadrato = sbil !== null && Math.abs(sbil) < 0.01;
  const parserMancante = detail.consulente_parser_status === "da_mappare";

  return (
    <div data-testid="dettaglio-page">
      <Header />
      <div className="pa-container" style={{ padding: "28px 24px 64px" }}>
        <button
          onClick={() => nav("/")}
          data-testid="back-to-dashboard"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--pa-muted)",
            fontSize: 13,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            padding: 0,
            marginBottom: 18,
          }}
        >
          <ArrowLeft size={14} /> Dashboard
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 24,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: "var(--pa-navy)",
                margin: 0,
                letterSpacing: "-0.01em",
              }}
              data-testid="dettaglio-title"
            >
              {detail.cliente_nome}
            </h1>
            <p style={{ color: "var(--pa-muted)", margin: "4px 0 10px", fontSize: 14 }}>
              {MESI[detail.mese]} {detail.anno} · {detail.cliente_codice}
            </p>
            <span
              className={
                "pa-consulente-badge" + (parserMancante ? " warn" : "")
              }
              data-testid="dettaglio-consulente-badge"
            >
              {parserMancante && <AlertTriangle size={11} />}
              {detail.consulente_nome}
            </span>
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "var(--pa-red-bg)",
              color: "var(--pa-red)",
              padding: "12px 14px",
              borderRadius: 8,
              marginBottom: 18,
              fontSize: 13,
              whiteSpace: "pre-wrap",
            }}
            data-testid="dettaglio-error"
          >
            {error}
          </div>
        )}

        {/* PDF Section */}
        <Section title="File PDF" data-testid="pdf-section">
          {detail.pdfs.length === 0 ? (
            <p style={{ color: "var(--pa-muted)", fontSize: 14, margin: "0 0 16px" }}>
              Nessun PDF caricato per questo periodo.
            </p>
          ) : (
            <div style={{ marginBottom: 16 }}>
              {detail.pdfs.map((p) => (
                <PdfRow key={p.id} pdf={p} onDelete={() => deletePdf(p.id)} />
              ))}
            </div>
          )}

          <div
            className={`pa-dropzone ${dragOver ? "dragover" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="pdf-dropzone"
          >
            <Upload size={22} color="#94a3b8" style={{ marginBottom: 6 }} />
            <div style={{ fontSize: 14, color: "var(--pa-text)", fontWeight: 500 }}>
              {uploading ? "Caricamento in corso…" : "Trascina qui i PDF o clicca per selezionarli"}
            </div>
            <div style={{ fontSize: 12, color: "var(--pa-muted)", marginTop: 4 }}>
              È possibile caricare più PDF in una sola volta.
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              style={{ display: "none" }}
              data-testid="pdf-file-input"
            />
          </div>
        </Section>

        {/* Excel section */}
        <Section title="Excel generato">
          {detail.status === "excel_pronto" && (
            <div
              className="pa-card"
              style={{ padding: 20, background: "#FBFCFD" }}
              data-testid="excel-ready-card"
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <FileSpreadsheet size={22} color="var(--pa-green)" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {detail.excel_filename}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--pa-muted)" }}>
                    Pronto per l'import in Profis
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                <Metric label="DARE" value={fmt(detail.quadratura_dare)} />
                <Metric label="AVERE" value={fmt(detail.quadratura_avere)} />
                <Metric
                  label="Sbilancio"
                  value={sbil !== null ? (sbil >= 0 ? "+" : "") + fmt(sbil) : "—"}
                  highlight={quadrato ? "ok" : "warn"}
                />
                <Metric label="Righe" value={detail.n_righe_contabili ?? "—"} />
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                {(() => {
                  const token = localStorage.getItem("pa_token") || "";
                  const url = `${API}/acquisizioni/${detail.id}/download?token=${encodeURIComponent(token)}`;
                  const openNewTab = (e) => {
                    e.preventDefault();
                    // window.open with _blank: more reliable than <a target=_blank> inside
                    // sandboxed iframes because it's an explicit user-gesture call.
                    window.open(url, "_blank", "noopener");
                  };
                  return (
                    <>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={detail.excel_filename || "paghe.xlsx"}
                        onClick={openNewTab}
                        className="pa-btn-primary"
                        data-testid="download-excel-button"
                        style={{
                          padding: "10px 16px",
                          borderRadius: 8,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          textDecoration: "none",
                        }}
                      >
                        <Download size={16} /> Scarica Excel
                      </a>
                      <button
                        onClick={generate}
                        disabled={generating}
                        data-testid="regenerate-excel-button"
                        style={{
                          padding: "10px 16px",
                          borderRadius: 8,
                          fontWeight: 600,
                          cursor: "pointer",
                          background: "transparent",
                          border: "1px solid var(--pa-border)",
                          color: "var(--pa-navy)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {generating ? <span className="pa-spinner" style={{ borderColor: "rgba(31,58,95,0.3)", borderTopColor: "var(--pa-navy)" }} /> : <RefreshCw size={14} />}
                        Rigenera
                      </button>
                      <div style={{ width: "100%", fontSize: 12, color: "var(--pa-muted)", marginTop: 4 }}>
                        Il download apre il file in una nuova scheda. Se il browser blocca le finestre pop-up, autorizzale per questo sito oppure clicca col tasto destro su "Scarica Excel" → <em>"Apri link in un'altra scheda"</em>.
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {detail.status !== "excel_pronto" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {detail.status === "acquisito_esterno" && (
                <div
                  style={{
                    background: "var(--pa-green-bg)",
                    color: "var(--pa-green)",
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontSize: 13,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  data-testid="external-flag-banner"
                >
                  <CheckSquare size={14} />
                  Questo mese è marcato come <strong>già acquisito esternamente</strong> (fuori dal portale).
                </div>
              )}
              {parserMancante && (
                <div
                  style={{
                    background: "var(--pa-amber-bg)",
                    color: "var(--pa-amber)",
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontSize: 13,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  data-testid="parser-missing-warning"
                >
                  <AlertTriangle size={14} />
                  Parser non ancora disponibile per "{detail.consulente_nome}".
                </div>
              )}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={generate}
                  disabled={generating || detail.pdfs.length === 0 || parserMancante}
                  className="pa-btn-accent"
                  data-testid="generate-excel-button"
                  style={{
                    padding: "12px 22px",
                    borderRadius: 8,
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    opacity:
                      detail.pdfs.length === 0 || parserMancante ? 0.55 : 1,
                  }}
                >
                  {generating ? <span className="pa-spinner" /> : <FileSpreadsheet size={16} />}
                  {generating ? "Generazione in corso…" : "Genera Excel"}
                </button>

                <button
                  onClick={toggleExternal}
                  data-testid="toggle-external-button"
                  style={{
                    padding: "12px 18px",
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: "pointer",
                    background: "transparent",
                    border: "1px solid var(--pa-border)",
                    color:
                      detail.status === "acquisito_esterno"
                        ? "var(--pa-muted)"
                        : "var(--pa-navy)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {detail.status === "acquisito_esterno" ? (
                    <>
                      <Square size={14} /> Annulla "già acquisito"
                    </>
                  ) : (
                    <>
                      <CheckSquare size={14} /> Segna come già acquisito
                    </>
                  )}
                </button>
              </div>
              {detail.pdfs.length === 0 && detail.status !== "acquisito_esterno" && (
                <p style={{ color: "var(--pa-muted)", fontSize: 13, margin: 0 }}>
                  Carica un PDF per generare l'Excel, oppure segna il mese come
                  già acquisito se lo hai gestito fuori dal portale.
                </p>
              )}
            </div>
          )}
        </Section>

        {/* Note e avvisi */}
        {detail.note_avvisi && detail.note_avvisi.length > 0 && (
          <Section title="Note e avvisi dalla scrittura">
            <ul style={{ margin: 0, paddingLeft: 18, color: "var(--pa-text)", fontSize: 14, lineHeight: 1.6 }}>
              {detail.note_avvisi.map((n, i) => (
                <li key={i} data-testid={`note-item-${i}`}>{n}</li>
              ))}
            </ul>
          </Section>
        )}

        {/* Logs (collapsible) */}
        <div
          className="pa-card"
          style={{ padding: 16, marginTop: 22 }}
          data-testid="logs-section"
        >
          <button
            onClick={() => setLogsOpen((v) => !v)}
            data-testid="toggle-logs"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--pa-text)",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: 0,
            }}
          >
            {logsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Log operazioni
          </button>
          {logsOpen && (
            <div style={{ marginTop: 12 }}>
              {logs.length === 0 ? (
                <p style={{ color: "var(--pa-muted)", fontSize: 13, margin: 0 }}>
                  Nessuna operazione registrata.
                </p>
              ) : (
                logs.map((l) => (
                  <div
                    key={l.id}
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px solid #F1F2F4",
                      display: "flex",
                      gap: 12,
                      fontSize: 13,
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 4,
                        minWidth: 70,
                        textAlign: "center",
                        background:
                          l.tipo === "errore"
                            ? "var(--pa-red-bg)"
                            : l.tipo === "warning"
                              ? "var(--pa-amber-bg)"
                              : "var(--pa-grey-bg)",
                        color:
                          l.tipo === "errore"
                            ? "var(--pa-red)"
                            : l.tipo === "warning"
                              ? "var(--pa-amber)"
                              : "var(--pa-muted)",
                      }}
                    >
                      {l.tipo}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "var(--pa-text)", whiteSpace: "pre-wrap" }}>
                        {l.messaggio}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--pa-muted)", marginTop: 2 }}>
                        {new Date(l.timestamp).toLocaleString("it-IT")}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <h2
        style={{
          fontSize: 13,
          fontWeight: 600,
          textTransform: "uppercase",
          color: "var(--pa-muted)",
          letterSpacing: "0.06em",
          marginBottom: 12,
        }}
      >
        {title}
      </h2>
      <div className="pa-card" style={{ padding: 20 }}>
        {children}
      </div>
    </section>
  );
}

function PdfRow({ pdf, onDelete }) {
  const sizeKb = (pdf.dimensione_byte / 1024).toFixed(0);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        background: "#FAFBFC",
        border: "1px solid var(--pa-border)",
        borderRadius: 8,
        marginBottom: 8,
      }}
      data-testid={`pdf-row-${pdf.id}`}
    >
      <FileText size={18} color="#6B7280" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 500,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {pdf.nome_originale}
        </div>
        <div style={{ fontSize: 12, color: "var(--pa-muted)" }}>
          {sizeKb} KB · {new Date(pdf.uploaded_at).toLocaleString("it-IT")}
        </div>
      </div>
      <button
        onClick={onDelete}
        data-testid={`pdf-delete-${pdf.id}`}
        title="Rimuovi"
        style={{
          background: "transparent",
          border: "none",
          color: "var(--pa-red)",
          cursor: "pointer",
          padding: 6,
          borderRadius: 6,
        }}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  const color =
    highlight === "ok"
      ? "var(--pa-green)"
      : highlight === "warn"
        ? "var(--pa-red)"
        : "var(--pa-navy)";
  return (
    <div
      style={{
        background: "white",
        border: "1px solid var(--pa-border)",
        borderRadius: 8,
        padding: "10px 12px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--pa-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 700,
          color,
          fontVariantNumeric: "tabular-nums",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
